import React, { FC } from "react";
import { useLabels } from "../Hooks/useLabels";
import { InformationCard } from "./InformationCard";
import { DoctorDto } from "@apiModels/DoctorDto.csharp";
import { LocationDto } from "@apiModels/LocationDto.csharp";
import { isLocationSelected } from "../Helpers/locationHelpers";

interface InformationCardWithFiltersResetProps {
  doctor: DoctorDto;
  location: LocationDto;
  onResetFilters: () => void;
}

export const InformationCardWithFiltersReset: FC<
  InformationCardWithFiltersResetProps
> = (props) => {
  const { doctor, location, onResetFilters } = props;

  const { bookingLabels } = useLabels();

  const isDoctorSelected = doctor.id && doctor.id != "-1";

  const defaultHeaders = {
    doctorAndLocation: `Viser tjenester vi tilbyr i $locationName hos $doctorName`,
    doctorOnly: `Viser tjenester $doctorName tilbyr`,
    locationOnly: `Viser tjenester vi tilbyr i $locationName`,
  };

  const defaultDescription =
    "Derfor kan det vises få valg. $clearLink om du vil se alle tjenestene Volvat tilbyr.";
  const defaultClearLinkLabel = "Klikk her";

  const getHeader = () => {
    if (isDoctorSelected && isLocationSelected(location)) {
      return replaceHeaderPlaceholders(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorAndLocationFilterInformationText ||
          defaultHeaders.doctorAndLocation
      );
    } else if (isDoctorSelected) {
      return replaceHeaderPlaceholders(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorFilterInformationText || defaultHeaders.doctorOnly
      );
    } else if (isLocationSelected(location)) {
      return replaceHeaderPlaceholders(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .locationFilterInformationText || defaultHeaders.locationOnly
      );
    } else {
      return "";
    }
  };

  const getClearLinkLabel = () => {
    if (isDoctorSelected && isLocationSelected(location)) {
      return (
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorAndLocationFilterInformationClearLinkLabel ||
        defaultClearLinkLabel
      );
    } else if (isDoctorSelected) {
      return (
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorFilterInformationClearLinkLabel || defaultClearLinkLabel
      );
    } else if (isLocationSelected(location)) {
      return (
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .locationFilterInformationClearLinkLabel || defaultClearLinkLabel
      );
    } else {
      return "";
    }
  };

  const getDescriptionParts = () => {
    if (isDoctorSelected && isLocationSelected(location)) {
      return getPartsFromDescriptionPlaceholder(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorAndLocationFilterInformationDescription || defaultDescription
      );
    } else if (isDoctorSelected) {
      return getPartsFromDescriptionPlaceholder(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .doctorFilterInformationDescription || defaultDescription
      );
    } else if (isLocationSelected(location)) {
      return getPartsFromDescriptionPlaceholder(
        bookingLabels?.stepsBookingLabels.newBookingStepsLabels
          .locationFilterInformationDescription || defaultDescription
      );
    } else {
      return [];
    }
  };

  const replaceHeaderPlaceholders = (label: string) => {
    return label
      .replace("$locationName", location.name)
      .replace("$doctorName", doctor.name);
  };

  const getPartsFromDescriptionPlaceholder = (label: string) => {
    return replaceHeaderPlaceholders(label).split("$clearLink");
  };

  const renderDescription = () => {
    const parts = getDescriptionParts();
    if (parts.length === 0) return null;
    if (parts.length === 1) return <>{parts[0]}</>;
    return (
      <>
        {parts[0]}
        <button type="button" onClick={onResetFilters}>
          {getClearLinkLabel()}
        </button>
        {parts[1]}
      </>
    );
  };

  return (
    <InformationCard heading={getHeader()} description={renderDescription()} />
  );
};
